// this is just a mediator that contains some pieces of logic that are common to all static tables with filters
// static tables refers to tables that are receiving all data at once (pagination or sorting are handled on the frontend)

import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

// components
import { DataTable } from './DataTable';
import { getFilterChips } from 'components/TableFilters/StaticTableFilters';

// types
import { IAppTableFilters } from 'services/AppTableFilters';

type StaticTableWithFiltersProps<T> = {
  appTableFiltersInstance: IAppTableFilters;
  columns: any[];
  data: T[];
  rowMapper?: (cells: any[]) => JSX.Element;
  isLoading?: boolean;
  disableResettingPaginationOnUpdate?: boolean;
  children?: React.ReactNode;
};

// appTableFiltersInstance is an instance of AppTableFilters class
export const StaticTableWithFilters = observer(
  <T extends object>({
    appTableFiltersInstance,
    rowMapper,
    columns,
    data,
    isLoading,
    disableResettingPaginationOnUpdate,
    children = null,
  }: StaticTableWithFiltersProps<T>) => {
    const [tableData, setTableData] = useState<T[]>([]);

    useEffect(() => {
      appTableFiltersInstance?.setData(data);
    }, [data]);

    useEffect(() => {
      appTableFiltersInstance && setTableData(appTableFiltersInstance.getFilteredData());
    }, [appTableFiltersInstance.data, appTableFiltersInstance.filters, appTableFiltersInstance.sorters]);

    return (
      <DataTable
        rowMapper={rowMapper}
        columns={columns}
        data={tableData}
        rowsPerPage={10}
        isLoading={isLoading}
        FiltersComponent={getFilterChips(appTableFiltersInstance)}
        disableResettingPaginationOnUpdate={disableResettingPaginationOnUpdate}
      >
        {children}
      </DataTable>
    );
  }
);
